/**
 * Constants + tiny pure helpers shared by SDK, API and dashboard.
 * ZOD-FREE — the SDK imports this file; nothing here may import zod.
 */
import type { AutoTrackConfig, PrivacyConfig } from './types';

/** Auto-tracked event vocabulary (custom events use their own normalized names). */
export const EVENT_NAMES = [
	'page_viewed',
	'page_left',
	'l3ns_loaded',
	'session_started',
	'user_returned',
	'user_identified',
	'user_signed_out',
	'form_viewed',
	'form_started',
	'form_abandoned',
	'form_completed',
	'field_focused',
	'field_completed',
	'field_error_seen',
	'field_corrected',
	'form_error',
	'button_clicked',
	'link_clicked',
	'zone_click',
	'zone_pick',
	'dead_click',
	'rage_click',
	'search_started',
	'search_submitted',
	'search_result_clicked',
	'filter_selected',
	'scroll_25',
	'scroll_50',
	'scroll_75',
	'scroll_100',
	'error_captured',
	'api_request_failed',
	'network_stall_detected',
	'web_vital',
	'funnel_started',
	'funnel_step',
	'funnel_completed',
] as const;
export type AutoEventName = (typeof EVENT_NAMES)[number];

export const LIMITS = {
	MAX_BODY_BYTES: 262_144,
	MAX_EVENTS_PER_ENVELOPE: 50,
	MAX_EVENT_NAME_LEN: 64,
	MAX_PROP_KEYS: 32,
	MAX_PROP_KEY_LEN: 64,
	MAX_PROP_VALUE_LEN: 8_192,
	MAX_URL_LEN: 2_048,
	MAX_STR_LEN: 512,
	/** Network waterfall entries per envelope (leftovers ride the next flush). */
	MAX_NET_PER_ENVELOPE: 40,
	/** Captured headers per side (request/response) and value length. */
	MAX_NET_HEADERS: 24,
	MAX_NET_HEADER_KEY_LEN: 64,
	MAX_NET_HEADER_VALUE_LEN: 256,
	/** Server-Timing entries per request. */
	MAX_NET_SERVER_TIMING: 10,
} as const;

/**
 * Identifier shapes shared by API validators, the shared zod schemas, and
 * dashboard query guards — one source so a rule/route can't accept a key the
 * schema that minted it would reject (or vice versa). Anchored, no flags.
 */
export const VARIABLE_KEY_PATTERN = /^[a-z][a-z0-9_-]{0,31}$/;
export const TAG_NAME_PATTERN = /^[a-z0-9_-]{1,64}$/;

/**
 * Signed-in user id / display name cap (session context `uid`/`uname`). A
 * standalone const, not a LIMITS key: the LIMITS object ships whole in the
 * ≤5 KB SDK core, a const inlines to its literal.
 */
export const MAX_USER_LEN = 128;

export const INGEST_PATH = '/v1/events';
export const CONFIG_PATH = '/v1/config';

/**
 * Field name/id/placeholder/autocomplete/type fragments marking a field
 * sensitive. Values are never read for ANY field; on top of that, clicks on a
 * sensitive field carry no text (clicks.ts) and its key is replaced by a
 * positional placeholder in the form fingerprint (forms.ts keyOf). Field
 * identity itself — key, type, digit-masked label — is site markup, not
 * visitor data, and is tracked normally so per-field analytics stay usable.
 */
export const SENSITIVE_FIELD_PATTERN =
	'password|passwd|pass|card|ccnum|cc-num|cc_num|cc-number|cvv|cvc|csc|token|secret|auth|passport|document|national|ssn|dob|birth|address';

/**
 * Contract for the future data-l3ns-allow flow (masked-unless-allowed fields).
 * Not consulted yet: current SDK behavior is strictly stronger — field values
 * are never read at all (see docs/PRIVACY.md "Controls" + README Known gaps).
 */
export const MASKED_FIELD_PATTERN = 'email|phone|tel|mobile';

/**
 * Query-string keys whose values are stripped from URLs before leaving the
 * browser. `signature|credential|\bsig\b` cover presigned-URL credentials
 * (X-Amz-Signature/-Credential, X-Goog-*, Azure SAS `sig`, CloudFront
 * Signature) — a captured resource URL with those intact is a replayable
 * grant to a private object, and the waterfall captures far more resource
 * URLs than events ever did. `sig` is word-bounded so `design=`/`signup=`
 * values survive; the rest match as substrings like the original keys.
 */
export const SENSITIVE_QUERY_PATTERN =
	'token|key|auth|secret|password|email|phone|code|session|signature|credential|\\bsig\\b';

/**
 * Header names whose VALUES never leave the browser (network waterfall
 * capture). Matched as a substring of the lowercased name so vendor prefixes
 * (x-api-key, x-amz-security-token, cf-access-jwt-…) are covered. Applied
 * client-side by the SDK and re-applied in the consumer (defense in depth).
 */
export const SENSITIVE_HEADER_PATTERN =
	'auth|cookie|token|secret|key|session|signature|credential|password|jwt|csrf|xsrf';

/**
 * Redacted copy of captured headers: sensitive values replaced, count and
 * lengths capped. Never throws; non-string values dropped.
 */
export function redactHeaders(h: Record<string, unknown>): Record<string, string> {
	const re = new RegExp(SENSITIVE_HEADER_PATTERN, 'i');
	const out: Record<string, string> = {};
	let n = 0;
	for (const k in h) {
		if (n >= LIMITS.MAX_NET_HEADERS) break;
		const v = h[k];
		if (typeof v != 'string') continue;
		const name = k.slice(0, LIMITS.MAX_NET_HEADER_KEY_LEN).toLowerCase();
		out[name] = re.test(name) ? 'REDACTED' : v.slice(0, LIMITS.MAX_NET_HEADER_VALUE_LEN);
		n++;
	}
	return out;
}

export const DEFAULT_AUTOTRACK: AutoTrackConfig = {
	pageViews: true,
	sessions: true,
	forms: true,
	funnels: true,
	clicks: true,
	scroll: true,
	errors: true,
	performance: true,
	search: true,
	network: true,
};

export const DEFAULT_PRIVACY: PrivacyConfig = {
	maskAllInputs: true,
	maskText: true,
	blockSensitiveFields: true,
	allowSelectors: [],
	blockSelectors: [],
};

export const DEFAULT_FLUSH_INTERVAL_MS = 5_000;
export const SESSION_IDLE_MS = 30 * 60_000;

/**
 * Lowercase, collapse non-alphanumerics to `_`, trim `_`, cap length.
 * Names must start with a letter (server schema) — digit-leading names get an
 * `x_` prefix instead of being silently dropped at ingest.
 */
export function normalizeEventName(raw: string): string {
	const n = raw
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
	return (/^[a-z]/.test(n) ? n : n ? `x_${n}` : n).slice(0, LIMITS.MAX_EVENT_NAME_LEN);
}

/**
 * Bridge payload paths (BridgeEvent.fields / the SDK's `keys` report):
 * dotted paths into an object payload (`error.code`); `$` is the payload
 * itself when it isn't an object (a game code string, a list of ids); a `[]`
 * suffix marks a list, whose scalar items ship comma-joined (`wcIds[]`,
 * `$[]`) or, for a list of objects, one field of every item (`items[].code`).
 */
export const BRIDGE_ROOT_PATH = '$';

/** Whether a bridge payload path names a list — its items were counted one by one. */
export const isBridgeListPath = (path: string): boolean => path.includes('[]');

/**
 * The prop key a bridge payload path ships under: `error.code` → `error_code`,
 * `wcIds[]` → `wcIds`, `items[].code` → `items_code`, `$` / `$[]` → `value`.
 */
export function bridgePropKey(path: string): string {
	const p = path.replace(/\[\]/g, '').replace(/^\$\.?/, '');
	return (p || 'value').replace(/\./g, '_');
}

/** Replace digits, cap length — the shared masking primitive. */
export function maskString(s: string, max = 64): string {
	return s.replace(/\d/g, '•').slice(0, max);
}

// -- form error messages ---------------------------------------------------------

/**
 * Error copy leaving the browser is the SITE's validation string, never
 * visitor data — but a site may echo the value back ("'foo' is not a valid
 * email"), so on top of maskString's digit masking, email-shaped tokens and
 * quoted fragments are redacted. Whitespace collapsed, capped. The SDK scrubs
 * with it; the dashboard re-derives an unmasked message's key with it.
 */
export function scrubMsg(s: string, max = 200): string {
	return maskString(
		s
			.replace(/\s+/g, ' ')
			.trim()
			.replace(/[^\s@'"“”«»<>]+@[^\s@'"“”«»<>]+/g, '[email]')
			.replace(/["“«][^"”»]{1,80}["”»]/g, '…')
			.replace(/(^|[\s(])'[^']{1,80}'(?=$|[\s).,;:!?])/g, '$1…'),
		max,
	);
}

/**
 * The key an operator unmasks a form error message by (FormConfig.unmask):
 * its scrubbed text, trimmed as stored, every masked digit run one `•` — "No
 * se encontró la transacción •••••••••." and its 10-digit twin are one message.
 */
export function unmaskKey(scrubbed: string): string {
	return scrubbed.trim().replace(/•+/g, '•');
}

/** Error messages one form may send unmasked (FormConfig.unmask). */
export const MAX_UNMASKED_MESSAGES = 50;

// -- form identity -------------------------------------------------------------
// A form's fingerprint hashes its field keys, so a single field whose name/id is
// regenerated per render fragments the form into a new registry row on every
// page load (seen in the wild: a CMS consent checkbox named
// "94441aca-….-36bf3daa3a80_1_formField_6_general0", fresh uuid each visit).
// normalizeFieldKey strips only unambiguously-random token shapes; for any key
// that contains none it MUST return the input unchanged, or every healthy form
// would re-key. Deliberately NOT reused for popup-anchor ids, whose ephemeral
// shapes are different (LAYER_14000) and looser.

/** Random-per-render token shapes: uuids, long hashes, counters. */
const EPHEMERAL_UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
/**
 * ≥16 contiguous hex chars containing a digit — undashed uuids and long
 * hashes. Deliberately ABOVE the 12-15 range where stable vendor ids live
 * (Salesforce 00N5000000ABCDE has an all-hex 12-run): stripping a STABLE id
 * conflates two real fields, while missing an ephemeral one only fragments
 * the fingerprint — which the ingest matcher heals. When in doubt, keep.
 */
const EPHEMERAL_HEX = /(?=[0-9a-f]*\d)[0-9a-f]{16,}/gi;
/** ≥10-digit runs (unix timestamps and up) — stable ids like question_1000001 survive. */
const EPHEMERAL_NUM = /\d{10,}/g;

/**
 * Field key with ephemeral tokens removed and separator debris collapsed.
 * '' when nothing letter-bearing survives — callers fall back to their next
 * key source. Identity for keys with no ephemeral tokens.
 */
export function normalizeFieldKey(key: string): string {
	const n = key
		.replace(EPHEMERAL_UUID, '')
		.replace(EPHEMERAL_HEX, '')
		.replace(EPHEMERAL_NUM, '')
		.replace(/[-_.:]{2,}/g, '_')
		.replace(/^[-_.:]+|[-_.:]+$/g, '');
	return /[a-z]/i.test(n) ? n.slice(0, 64) : '';
}

/**
 * Auto-fingerprint keys end in '-' + an EXACTLY-6-hex djb2 chunk (the SDK pads
 * short hashes); the part before it is the stable base (id/name/action path/
 * page path). Used to gather same-form candidates for ingest-time matching.
 * Keys without the suffix shape — explicit data-l3ns-form values like
 * "promo-1" or "My Onboarding" — are returned whole and never fuzzy-matched
 * away. Exactly six, not 1-6: operator keys routinely end in short hex-looking
 * chunks ("-1", "-2b", "-cafe"), and misclassifying one lets the matcher merge
 * forms the operator explicitly split; a full 6-hex accidental suffix is rare
 * enough to accept.
 */
export const AUTO_FORM_KEY = /-[0-9a-f]{6}$/;
export function formKeyBase(formKey: string): string {
	return formKey.replace(AUTO_FORM_KEY, '');
}

/**
 * The key a field is STORED and displayed under: normalized when ephemeral
 * tokens were present, the raw key when normalization would erase it entirely.
 * Ingest writes, merge folds and signatures must all agree on this exact
 * function — divergence re-fragments the rollups the matcher just healed.
 */
export function storedFieldKey(key: string): string {
	return normalizeFieldKey(key) || key;
}

/** Structural-selector key bases (keyOf's last resort) — never shown as names. */
const STRUCTURAL_BASE =
	/^(div|span|section|ul|ol|li|form|article|aside|main|footer|header|nav|dialog)[_-]/;

/**
 * Human name for a form, best evidence first: operator displayName, captured
 * name/heading, then the humanized key base ('banco_depositar-d55a6c' →
 * 'Banco depositar'). Structural-selector bases read as noise → 'Unnamed form'.
 */
export function prettyFormName(f: {
	formKey: string;
	name?: string | null;
	displayName?: string | null;
}): string {
	if (f.displayName) return f.displayName;
	if (f.name) return f.name;
	const base = formKeyBase(f.formKey);
	if (!base || STRUCTURAL_BASE.test(base)) return 'Unnamed form';
	const words = base.replace(/[_-]+/g, ' ').trim();
	return words ? words[0].toUpperCase() + words.slice(1) : f.formKey;
}

/**
 * Matching signature for one field: normalized, with a trailing _N index
 * stripped so order-sensitive fallback keys (text_0, legacy redacted_2) line up when a
 * conditional field shifts everything after it. Comparison-only — stored field
 * keys keep their indexes.
 */
export function fieldSignature(key: string): string {
	return storedFieldKey(key).replace(/_\d{1,3}$/, '');
}

/** Signature MULTISET — twin fields (redacted×3) keep their count. */
function signatureCounts(keys: string[]): Map<string, number> {
	const m = new Map<string, number>();
	for (const k of keys) {
		const s = fieldSignature(k);
		m.set(s, (m.get(s) ?? 0) + 1);
	}
	return m;
}

/**
 * Are two field-key sets the same logical form (given an equal key base)?
 *  'near'      — multisets nearly identical (Jaccard ≥ 0.8, ≥3 distinct
 *                fields of evidence): trust unconditionally; localized
 *                names/headings must not veto this tier.
 *  'contained' — ≥90% of the smaller multiset appears in the larger, ≥2
 *                shared fields, ≤3 extras: the conditional-fields case (a
 *                deposit form with/without the saved-account picker). Weaker
 *                evidence — callers veto on conflicting captured names.
 * Multisets, not sets: a login [user, redacted_0] and a change-password
 * [user, redacted_0, redacted_1] must NOT collapse to one {user, redacted}
 * set and read as identical — twin counts are real evidence.
 */
export function similarFieldSets(aKeys: string[], bKeys: string[]): 'near' | 'contained' | null {
	const a = signatureCounts(aKeys);
	const b = signatureCounts(bKeys);
	if (a.size === 0 || b.size === 0) return null;
	let inter = 0;
	let union = 0;
	for (const [k, n] of a) {
		const m = b.get(k) ?? 0;
		inter += Math.min(n, m);
		union += Math.max(n, m);
	}
	for (const [k, m] of b) if (!a.has(k)) union += m;
	let aTotal = 0;
	let bTotal = 0;
	for (const n of a.values()) aTotal += n;
	for (const m of b.values()) bTotal += m;
	const small = Math.min(aTotal, bTotal);
	const big = Math.max(aTotal, bTotal);
	if (inter / union >= 0.8 && union >= 3) return 'near';
	if (inter / small >= 0.9 && small >= 2 && big - inter <= 3) return 'contained';
	return null;
}

/**
 * Display fallback for zone items collected before labels existed:
 * '/casino-en-vivo' → 'Casino en vivo', '/' → 'Home'. Non-path items
 * (text, selectors) pass through untouched.
 */
export function prettyZoneItem(item: string): string {
	if (item[0] !== '/') return item;
	const words = item
		.replace(/^\/+|\/+$/g, '')
		.replace(/[-_/]+/g, ' ')
		.trim();
	if (!words) return 'Home';
	return words[0].toUpperCase() + words.slice(1);
}

/**
 * Strip values of sensitive query params — in the query string AND in a
 * param-shaped hash fragment. The fragment matters because OAuth implicit
 * flows and magic links deliberately put tokens there (`#access_token=…`) so
 * they never reach a server; without this, the auth-callback page URL is
 * stamped onto every event and every waterfall row. Hash ROUTES (`#/settings`,
 * `#pricing`) are left untouched — only `k=v` pairs are rewritten. Never throws.
 */
export function sanitizeUrl(url: string): string {
	try {
		const u = new URL(url);
		const re = new RegExp(SENSITIVE_QUERY_PATTERN, 'i');
		const scrub = (p: URLSearchParams): boolean => {
			let hit = false;
			p.forEach((_v, k) => {
				if (re.test(k)) {
					p.set(k, 'REDACTED');
					hit = true;
				}
			});
			return hit;
		};
		let changed = scrub(u.searchParams);
		const h = u.hash;
		if (h.indexOf('=') > 0) {
			// '#' or a route prefix ('#/cb?') comes first; the rest is k=v pairs
			const q = h.indexOf('?') > 0 ? h.indexOf('?') + 1 : 1;
			const p = new URLSearchParams(h.slice(q));
			if (scrub(p)) {
				u.hash = h.slice(0, q) + p;
				changed = true;
			}
		}
		const out = changed ? u.toString() : url;
		return out.length > LIMITS.MAX_URL_LEN ? out.slice(0, LIMITS.MAX_URL_LEN) : out;
	} catch {
		return url.slice(0, LIMITS.MAX_URL_LEN);
	}
}
